import { useCallback, useMemo, useState } from 'react';
import { exportCreditAdviceDetailColumns, getChangingValueCreditAdviceColumns } from '../cols-definition';
import type { ChiTietGiayBaoCo, GiayBaoCo, GiayBaoCoInput } from '@/types/schemas/giay-bao-co.type';
import { useInvoiceParamsPresence, useMultipleForms } from '../hooks/useMultipleForms';
import type { DataTableColumn } from '@/components/custom/erp/data-table-v2';
import { initialSearchValues, SearchFormValues } from '../schemas';
import { TABLE_PAGE_SIZE } from '../constants';
import { useCRUD, useForms } from '@/hooks';
import { useSearchTable } from '../hooks';
import { QUERY_KEYS } from '@/constants';

export const useGiayBaoCoTable = () => {
  const hasInvoiceParams = useInvoiceParamsPresence();
  const [searchParams, setSearchParams] = useState<SearchFormValues>(initialSearchValues);
  const crud = useCRUD<GiayBaoCo, GiayBaoCoInput>({
    endpoint: QUERY_KEYS.GIAY_BAO_CO,
    pageSize: TABLE_PAGE_SIZE,
    initialFetch: !hasInvoiceParams,
    sumField: 't_tien_nt',
    useFilter: true
  });
  const form = useForms<GiayBaoCo>(crud.fetchDetail);
  const searchTable = useSearchTable({
    searchData: crud.searchData,
    searchParams,
    setSearchParams,
    handleViewClick: form.handleViewClick,
    sumTotal: crud.sumTotal,
    isFiltered: crud.isFiltered,
    handlePageChange: crud.handlePageChange
  });
  const multipleForms = useMultipleForms({ addItem: crud.addItem, editData: crud.updateItem });

  const handleSearchSubmit = useCallback(
    async (filters: SearchFormValues) => {
      setSearchParams(filters);
      await searchTable.handleInitialSearch(filters);
    },
    [searchTable]
  );

  const handleRefresh = useCallback(async () => {
    if (searchTable.hasFilterParams) {
      await searchTable.handleRefreshClick();
    } else if (crud.isFiltered) {
      await crud.searchData(searchParams);
    } else {
      await crud.refreshData();
    }

    if (form.selectedObj?.uuid) {
      await crud.fetchDetail(form.selectedObj.uuid);
    }
  }, [crud, form.selectedObj?.uuid, searchParams, searchTable]);

  const displayData = useMemo(
    () => (searchTable.hasFilterParams ? searchTable.searchState.searchResults : crud.data || []),
    [crud.data, searchTable.hasFilterParams, searchTable.searchState.searchResults]
  );
  const totalAmount = searchTable.hasFilterParams
    ? (searchTable.searchState.totalRow?.t_tien_nt ?? crud.sumTotal)
    : crud.sumTotal;
  const columns = useMemo(
    () => getChangingValueCreditAdviceColumns(form.handleViewClick, totalAmount) as DataTableColumn[],
    [form.handleViewClick, totalAmount]
  );
  const tables = useMemo(
    () => [
      {
        name: 'Tất cả',
        rows: displayData,
        columns
      }
    ],
    [columns, displayData]
  );

  return {
    crud,
    form,
    searchDialog: {
      open: form.showSearch,
      searchParams,
      onOpen: form.handleSearch,
      onClose: form.handleCloseSearch,
      onSubmit: handleSearchSubmit
    },
    multipleFormDialog: {
      open: multipleForms.showMultipleForms,
      initialData: multipleForms.initialData,
      invoiceData: multipleForms.invoiceData,
      currentFormIndex: multipleForms.currentFormIndex,
      formMode: multipleForms.formMode,
      onSubmit: multipleForms.handleMultipleFormSubmit,
      onClose: multipleForms.handleCloseMultipleForms
    },
    table: {
      tables,
      columns,
      displayData,
      displayIsLoading: crud.isLoading || crud.isSearching || searchTable.isFiltering,
      currentPage: searchTable.hasFilterParams ? searchTable.searchState.currentPage : crud.currentPage,
      totalItems: searchTable.hasFilterParams ? searchTable.searchState.totalCount : crud.totalItems,
      onPageChange: searchTable.onPageChange,
      onSearchChange: searchTable.handleSearchChange,
      onRefresh: handleRefresh
    },
    detailTable: {
      rows: form.selectedObj ? ((crud.detail as GiayBaoCo | null)?.chi_tiet_giay_bao_co_data ?? []) : [],
      columns: exportCreditAdviceDetailColumns
    } satisfies { rows: ChiTietGiayBaoCo[]; columns: typeof exportCreditAdviceDetailColumns },
    selection: {
      selectedObj: form.selectedObj,
      selectedRowIndex: form.selectedRowIndex,
      clearSelection: form.clearSelection
    }
  };
};
